import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types
export type FormFieldType =
  | 'text'
  | 'number'
  | 'email'
  | 'date'
  | 'select'
  | 'checkbox'
  | 'password'
  | 'radio';

// Add Parameter types
export type ParameterType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array';

export interface Parameter {
  name: string;
  type: ParameterType;
  description: string;
  required: boolean;
}

export interface FormField {
  name: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  placeholder?: string;
  options?: string[]; // For select fields
}

export interface FormConfig {
  formTitle: string;
  formDescription: string;
  submitButtonText: string;
  successMessage: string;
}

export interface WebFormState {
  functionName: string;
  functionDescription: string;
  formConfig: FormConfig;
  formFields: FormField[];
  parameters: Parameter[]; // Add parameters to state
}

const initialState: WebFormState = {
  functionName: '',
  functionDescription: '',
  formConfig: {
    formTitle: '',
    formDescription: '',
    submitButtonText: 'Submit',
    successMessage: '',
  },
  formFields: [],
  parameters: [], // Initialize empty parameters array
};

export const webFormSlice = createSlice({
  name: 'webForm',
  initialState,
  reducers: {
    // Function details
    setFunctionName: (state, action: PayloadAction<string>) => {
      state.functionName = action.payload;
    },
    setFunctionDescription: (state, action: PayloadAction<string>) => {
      state.functionDescription = action.payload;
    },

    // Form configuration
    setFormTitle: (state, action: PayloadAction<string>) => {
      state.formConfig.formTitle = action.payload;
    },
    setFormDescription: (state, action: PayloadAction<string>) => {
      state.formConfig.formDescription = action.payload;
    },
    setSubmitButtonText: (state, action: PayloadAction<string>) => {
      state.formConfig.submitButtonText = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.formConfig.successMessage = action.payload;
    },
    updateFormConfig: (state, action: PayloadAction<Partial<FormConfig>>) => {
      state.formConfig = {
        ...state.formConfig,
        ...action.payload,
      };
    },

    // Parameter management
    addParameter: (state, action: PayloadAction<Parameter>) => {
      state.parameters.push(action.payload);
    },
    updateParameter: (
      state,
      action: PayloadAction<{ index: number; parameter: Parameter }>
    ) => {
      const { index, parameter } = action.payload;
      if (index >= 0 && index < state.parameters.length) {
        state.parameters[index] = parameter;
      }
    },
    removeParameter: (state, action: PayloadAction<number>) => {
      state.parameters.splice(action.payload, 1);
    },

    // Form fields
    addFormField: (state, action: PayloadAction<FormField>) => {
      state.formFields.push(action.payload);
    },
    updateFormField: (
      state,
      action: PayloadAction<{ index: number; field: FormField }>
    ) => {
      const { index, field } = action.payload;
      if (index >= 0 && index < state.formFields.length) {
        state.formFields[index] = field;
      }
    },
    removeFormField: (state, action: PayloadAction<number>) => {
      state.formFields.splice(action.payload, 1);
    },
    reorderFormField: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>
    ) => {
      const { fromIndex, toIndex } = action.payload;
      if (
        fromIndex >= 0 &&
        fromIndex < state.formFields.length &&
        toIndex >= 0 &&
        toIndex < state.formFields.length
      ) {
        const [movedField] = state.formFields.splice(fromIndex, 1);
        state.formFields.splice(toIndex, 0, movedField);
      }
    },

    // Field options (for select fields)
    addFieldOption: (
      state,
      action: PayloadAction<{ fieldIndex: number; option: string }>
    ) => {
      const { fieldIndex, option } = action.payload;
      if (
        fieldIndex >= 0 &&
        fieldIndex < state.formFields.length &&
        (state.formFields[fieldIndex].type === 'select' ||
          state.formFields[fieldIndex].type === 'radio' ||
          state.formFields[fieldIndex].type === 'checkbox')
      ) {
        if (!state.formFields[fieldIndex].options) {
          state.formFields[fieldIndex].options = [];
        }
        state.formFields[fieldIndex].options?.push(option);
      }
    },
    removeFieldOption: (
      state,
      action: PayloadAction<{ fieldIndex: number; optionIndex: number }>
    ) => {
      const { fieldIndex, optionIndex } = action.payload;
      if (
        fieldIndex >= 0 &&
        fieldIndex < state.formFields.length &&
        state.formFields[fieldIndex].options &&
        optionIndex >= 0 &&
        optionIndex < (state.formFields[fieldIndex].options?.length || 0)
      ) {
        state.formFields[fieldIndex].options?.splice(optionIndex, 1);
      }
    },

    // Reset
    resetWebForm: () => initialState,
  },
});

export const {
  setFunctionName,
  setFunctionDescription,
  setFormTitle,
  setFormDescription,
  setSubmitButtonText,
  setSuccessMessage,
  updateFormConfig,
  addParameter,
  updateParameter,
  removeParameter,
  addFormField,
  updateFormField,
  removeFormField,
  reorderFormField,
  addFieldOption,
  removeFieldOption,
  resetWebForm,
} = webFormSlice.actions;

export default webFormSlice.reducer;
